// 저장된 채점 결과 한 명분을 PATCH /api/scoring/history/{id}/result/{index} 형식으로 고치는 순수 함수.
// 서버(utils/scoring_record_edit.py)도 점수를 0~만점으로 자르고 합계를 다시 계산한다.
import { NAME_SOURCE, isPlaceholderStudentName } from '../../utils/studentNamePolicy';

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const sumScores = (items = []) => items.reduce((sum, item) => sum + toNumber(item?.score), 0);

export const validateEditedScore = (rawValue, maxScore) => {
  const text = String(rawValue ?? '').trim();
  const max = toNumber(maxScore);
  const rangeMessage = max > 0 ? `0~${max}점 사이로 입력해 주세요` : '0점 이상으로 입력해 주세요';
  if (!text) return { ok: false, message: rangeMessage };
  const score = Number(text);
  if (!Number.isFinite(score)) return { ok: false, message: '점수는 숫자로 입력해 주세요' };
  if (score < 0 || (max > 0 && score > max)) return { ok: false, message: rangeMessage };
  return { ok: true, score };
};

export const buildStudentNameEdit = (rawName) => {
  const name = String(rawName ?? '').replace(/\s+/g, ' ').trim();
  if (!name) return { error: '이름을 입력해 주세요' };
  if (name.length > 40) return { error: '이름은 40자까지 입력할 수 있어요' };
  return {
    updates: {
      studentName: name,
      student_name: name,
      revision: { mode: 'manual_edit', field: 'studentName', instruction: '검토 화면에서 학생 이름 수정' },
    },
  };
};

export const buildFeedbackEdit = (field, text) => ({
  updates: {
    [field]: String(text ?? ''),
    revision: { mode: 'manual_edit', field, instruction: '검토 화면에서 직접 수정' },
  },
});

const reasonFields = (current, reason) => {
  if (reason === undefined) return {};
  const changed = String(reason ?? '').trim() !== String(current?.reason ?? '').trim();
  return { reason, ...(changed ? { reason_edited: true } : {}) };
};

const editItem = (item, { score, reason, matchedLevel }) => ({
  ...item,
  score,
  ...reasonFields(item, reason),
  ...(matchedLevel !== undefined ? { matched_level: matchedLevel } : {}),
});

export const isTeacherEditedReason = (item) => Boolean(item?.reason_edited);

// questionIndex 가 있으면 다문항 결과(question_results[q].item_scores[i] 또는 문항 점수 자체)를 고친다.
export const buildItemScoreEdit = (result, { questionIndex = null, itemIndex = null, score: rawScore, reason, matchedLevel }) => {
  if (questionIndex !== null && questionIndex !== undefined) {
    const questions = Array.isArray(result?.question_results) ? result.question_results : [];
    const question = questions[questionIndex];
    if (!question) return { error: '고칠 문항을 찾지 못했어요' };
    const items = Array.isArray(question.item_scores) ? question.item_scores : [];
    const target = itemIndex !== null && itemIndex !== undefined ? items[itemIndex] : null;
    if (itemIndex !== null && itemIndex !== undefined && !target) return { error: '고칠 평가 요소를 찾지 못했어요' };
    const checked = validateEditedScore(rawScore, target ? target.max_score : question.max_score);
    if (!checked.ok) return { error: checked.message };

    let nextQuestion;
    if (target) {
      const nextItems = items.map((item, index) => (index === itemIndex ? editItem(item, { score: checked.score, reason, matchedLevel }) : item));
      nextQuestion = { ...question, item_scores: nextItems, score: sumScores(nextItems) };
    } else {
      nextQuestion = { ...question, score: checked.score, ...reasonFields(question, reason) };
    }
    const nextQuestions = questions.map((q, index) => (index === questionIndex ? nextQuestion : q));
    return {
      updates: {
        question_results: nextQuestions,
        revision: {
          mode: 'manual_edit',
          field: 'question_results',
          question_index: questionIndex,
          ...(target ? { item_index: itemIndex } : {}),
          instruction: '검토 화면에서 문항 점수·근거 수정',
        },
      },
    };
  }

  const items = Array.isArray(result?.item_scores) ? result.item_scores : [];
  const target = items[itemIndex];
  if (!target) return { error: '고칠 평가 요소를 찾지 못했어요' };
  const checked = validateEditedScore(rawScore, target.max_score);
  if (!checked.ok) return { error: checked.message };
  const nextItems = items.map((item, index) => (index === itemIndex ? editItem(item, { score: checked.score, reason, matchedLevel }) : item));
  return {
    updates: {
      item_scores: nextItems,
      revision: {
        mode: 'manual_edit',
        field: 'item_scores',
        item_index: itemIndex,
        instruction: `${target.item_name || '평가요소'} 검토 화면에서 직접 수정`,
      },
    },
  };
};

// 저장 전(기록 없음) 화면 반영용. 저장된 기록이면 서버 응답 result 로 바꾼다.
export const applyResultEditLocally = (result, updates = {}) => {
  const { revision, ...fields } = updates;
  const next = { ...(result || {}), ...fields };
  const editedName = fields.studentName ?? fields.student_name;
  if (editedName !== undefined) {
    next.name_source = isPlaceholderStudentName(editedName) ? NAME_SOURCE.PLACEHOLDER : NAME_SOURCE.TEACHER;
    delete next.name_conflict;
  }
  if (Array.isArray(fields.question_results)) {
    const total = sumScores(fields.question_results);
    next.total_score = total;
    next.score = total;
  } else if (Array.isArray(fields.item_scores)) {
    const total = sumScores(fields.item_scores);
    next.total_score = total;
    next.score = total;
  }
  return next;
};
